import type { ReactElement } from 'react';
import { useMemo, useCallback, useEffect } from 'react';

import { useTracking } from '@skyscanner/strevda-tracking';
import BpkGraphicPromo from '@skyscanner-internal/backpack-web/bpk-component-graphic-promotion';
import { BpkBox } from '@skyscanner-internal/backpack-web/bpk-component-layout';
import { useAdvertVisibilityTracking } from '@web-platform/shared-banana-redux/src/helpers/hooks/useAdvertVisibilityTracking';
import { useEverywhereSearchNavigation } from '@web-platform/shared-banana-redux/src/helpers/navigationHandlers/useEverywhereSearchNavigation';
import { EFS_GRAPHIC_PROMO_EVERYWHERE_CTA_URL } from '@web-platform/shared-universal-constants/src/adverts';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getUserDevice } from '@web-platform/shared-universal-redux/src/state';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { logOperationalEvent } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { compressGraphicPromoUrl } from '@web-platform/shared-universal-utils-universal/src/adverts/adverts';
import { useSelector } from 'react-redux';
import { redirect } from 'saddlebag-browser';

import type { BpkGraphicPromoProps } from '@skyscanner-internal/backpack-web/bpk-component-graphic-promotion';
import type { GraphicPromo } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/graphicPromo';

import STYLES from './GraphicPromoCard.module.scss';

export type Props = {
  advert: GraphicPromo;
};

/**
 * Not all devices are served images from EFS and it also depends on the headers we pass. This function ensures that
 * there is a suitable image for each device, by assigning them in descending order of suitability.
 *
 * @param {object} images The background images properties from the Creative returned by EFS.
 * @param {boolean} isMobileDevice Whether the user agent is mobile.
 * @returns {object} A CSS style object with custom properties for the background images.
 */

const getBackgroundStyle = (
  images: {
    backgroundImageDesktop?: string;
    backgroundImageMobile?: string;
  },
  isMobileDevice: boolean,
) => {
  const enableCompressGraphicPromo = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.OPS.ENABLE_COMPRESS_GRAPHIC_PROMO_MOBILE,
  );

  const desktopImg = images.backgroundImageDesktop ?? images.backgroundImageMobile;
  const mobileImg =
    // only compress mobile image when the user_agent is mobile and enableCompressGraphicPromo is true
    (isMobileDevice && enableCompressGraphicPromo
      ? compressGraphicPromoUrl(images.backgroundImageMobile)
      : images.backgroundImageMobile) ?? images.backgroundImageDesktop;

  return {
    '--background-image-desktop': `url(${desktopImg})`,
    '--background-image-mobile': `url(${mobileImg})`,
  };
};

const mapTextAlign = (textAlign: string): BpkGraphicPromoProps['textAlign'] => {
  switch (textAlign) {
    case 'left':
      return 'start';

    case 'right':
      return 'end';

    default:
      return 'center';
  }
};

export const GraphicPromoCard = ({ advert }: Props): ReactElement => {
  const { ref: trackingRef, trackClick } = useTracking(
    advert,
    {},
    false,
    getStrevdaTrackingConfig(),
  );
  const { ref: visibilityTrackerRef } = useAdvertVisibilityTracking();
  const combinedRef = useCallback(
    (el: HTMLDivElement) => {
      visibilityTrackerRef.current = el;
      trackingRef(el);
    },
    [trackingRef, visibilityTrackerRef],
  );

  const { analyticsProperties, creative } = advert;
  const { handleEverywhereSearchNavigation } = useEverywhereSearchNavigation({
    tracking: { trackInteractionEvent },
  });
  const { isMobilePhone } = useSelector(getUserDevice);

  useEffect(() => {
    const adLoadedPayload = {
      creativeId: analyticsProperties.idCreative,
      ctaUrl: creative.ctaUrl,
    };

    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_LOADED.NAME,
      strings: adLoadedPayload,
    });

    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_LOADED.NAME,
      ...adLoadedPayload,
    });
  }, [analyticsProperties.idCreative, creative.ctaUrl]);

  const onClick = useCallback(() => {
    trackClick();

    const adTappedPayload = {
      creativeId: analyticsProperties.idCreative,
      ctaUrl: creative.ctaUrl,
    };

    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_TAPPED.NAME,
      strings: adTappedPayload,
    });

    logOperationalEvent({
      eventName: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_TAPPED.NAME,
      ...adTappedPayload,
    });

    if (creative.ctaUrl.toLowerCase() === EFS_GRAPHIC_PROMO_EVERYWHERE_CTA_URL.toLowerCase()) {
      handleEverywhereSearchNavigation();
    } else {
      redirect(creative.ctaUrl);
    }
  }, [
    trackClick,
    analyticsProperties.idCreative,
    creative.ctaUrl,
    handleEverywhereSearchNavigation,
  ]);

  const graphicPromoProps: BpkGraphicPromoProps = useMemo(
    () => ({
      ...creative,
      sponsor: creative.sponsor && {
        ...creative.sponsor,
        label: translate('label_homepage_sponsored_card'),
      },
      textAlign: mapTextAlign(creative.textAlign),
      onClick,
      // TODO: CLOV-1913 className cannot be fully removed: BpkGraphicPromo has no internal support
      // for --background-image-* CSS custom properties, so background-image rules must be applied
      // externally via className. This is BpkGraphicPromo's official usage pattern (Backpack's own
      // stories use the same className + style combination). Deferred pending Backpack support.
      className: STYLES.graphicPromoCard,
      style: {
        ...getBackgroundStyle(creative, isMobilePhone),
        ...(creative.textColor && { color: creative.textColor }),
        ...(creative.backgroundColour && { backgroundColor: creative.backgroundColour }),
      },
      contentId: `graphic-promo__${analyticsProperties.idPlacement}`,
    }),
    [creative, analyticsProperties, isMobilePhone, onClick],
  );

  return (
    <BpkBox data-testid="GraphicPromoCard" ref={combinedRef}>
      <BpkGraphicPromo {...graphicPromoProps} />
    </BpkBox>
  );
};
